import { Component, Input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { PercentFormatInfo } from '@sb/library/data-models';
import { LocalizationPercentNoSymbolPipe, LocalizationPercentPipe } from '@sb/library/localization';
import { FormFieldNumberComponent } from '../number/form-field-number.component';

@Component({
    selector: 'lib-input-percent',
    templateUrl: './form-field-percent.component.html',
    styleUrls: ['./form-field-percent.component.scss']
})
export class FormFieldPercentComponent extends FormFieldNumberComponent implements OnInit {

    readonly MIN_PERCENT_LIMIT: number = 0;
    readonly MAX_PERCENT_LIMIT: number = 100;

    @Input() override minDecimalPlaces: number = 2; // ??
    @Input() override maxDecimalPlaces: number = 2;

    private _PercentFormatInfo!: PercentFormatInfo;

    private _percentPattern = 'n◊';
    private _regexPercentSymbolPlacement = /^n/;
    private _percentSymbolRightPlacement = true;
    private _percentFormatter!: LocalizationPercentNoSymbolPipe;

    private _min: number = this.MIN_PERCENT_LIMIT;
    private _max: number = this.MAX_PERCENT_LIMIT;
    private _initValue: number = this.MIN_PERCENT_LIMIT;

    constructor() {
        super();

        this._PercentFormatInfo = this._LocalizationService.PercentFormatInfo;
        this._NumberFormatInfo = this._LocalizationService.PercentFormatInfo;
        this._percentFormatter = new LocalizationPercentNoSymbolPipe();

        let formatter = new LocalizationPercentPipe();

        this.errors.Override({
            min: (value: {min: number, actual: number}) =>
                $localize`:@@CommonPercentValueTooSmallInclusive:Please enter an amount greater than or equal to ${formatter.transform(value.min, this.minDecimalPlaces, this.maxDecimalPlaces)}.`,
            minExclusive: (value: {min: number, actual: number}) =>
                $localize`:@@CommonPercentValueTooSmallExclusive:Please enter an amount greater than ${formatter.transform(value.min, this.minDecimalPlaces, this.maxDecimalPlaces)}.`,
            max: (value: {max: number, actual: number}) =>
                $localize`:@@CommonPercentValueTooLargeInclusive:Please enter an amount less than or equal to ${formatter.transform(value.max, this.minDecimalPlaces, this.maxDecimalPlaces)}.`,
            maxExclusive: (value: {max: number, actual: number}) =>
                $localize`:@@CommonPercentValueTooLargeExclusive:Please enter an amount less than ${formatter.transform(value.max, this.minDecimalPlaces, this.maxDecimalPlaces)}.`
        });
    }

    override ngOnInit() {
        super.ngOnInit();
        this.GetRegexForValidation();
        this.GetPercentSymbolPlacement();
    }

    override ngAfterViewInit(): void {
        super.ngAfterViewInit();
        this._min = this.GetMinValidatorValue();
        this._max = this.GetMaxValidatorValue();
        this._initValue = this.formControl.value ?? this._min;
    }

    private GetRegexForValidation(): void {
        let decimalSeparator = this._PercentFormatInfo.DecimalSeparator;

        //Follow this format ^(\d+)(\.\d{0,4})?$
        let decimalRegex: string;
        if (this.maxDecimalPlaces > 0) {
            decimalRegex = `^(\\d+(\\${decimalSeparator}\\d{0,${this.maxDecimalPlaces}})?|\\${decimalSeparator}\\d{0,${this.maxDecimalPlaces}})$`;
        } else {
            decimalRegex = `^(\\d+)$`;
        }

        this._validationRegex = new RegExp(decimalRegex, 'g');
    }

    override ValueFormatter(numberValue: number): string {
        return this._percentFormatter.transform(numberValue, this.minDecimalPlaces, this.maxDecimalPlaces);
    }

    private GetPercentSymbolPlacement(): void {
        this._percentPattern = this._PercentFormatInfo.PositivePattern!.trim();
        this._percentSymbolRightPlacement = this._regexPercentSymbolPlacement.test(this._percentPattern);
    }

    private GetMaxValidatorValue(): number {
        const validator = this.formControl.validator;
        if (validator === null) {
            return this.MAX_PERCENT_LIMIT;
        }

        const errors = validator(new FormControl(Infinity)) ?? {};
        return "max" in errors && errors.max.max < this.MAX_PERCENT_LIMIT ? errors.max.max : this.MAX_PERCENT_LIMIT;
    }

    private GetMinValidatorValue(): number {
        const validator = this.formControl.validator;
        if (validator === null) {
            return this.MIN_PERCENT_LIMIT;
        }

        const errors = validator(new FormControl(-Infinity)) ?? {};
        return "min" in errors && errors.min.min > this.MIN_PERCENT_LIMIT ? errors.min.min : this.MIN_PERCENT_LIMIT;
    }

    override OnBlur() {
        super.OnBlur();

        if (!this.formControl.valid) {
            if (this.formControl.value === null || this.formControl.value === undefined) {
                this.SetValue(this._initValue);
            }
        }
    }

    private SetValue(value: number) {
        this.formControl.isDirty = true;
        this.formControl.setValue(value);
        this.change.emit(value);
    }

    get percentSymbolRightPlacement(): boolean {
        return this._percentSymbolRightPlacement;
    }

    get percentSymbol(): string {
        return this._PercentFormatInfo.PercentSymbol!;
    }

}
